import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
  Typography,
  Alert,
  TextField,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const StatusBadge = ({ statut }) => {
  if (!statut) return <Chip label="Inconnu" size="small" />;
  const txt = statut.charAt(0).toUpperCase() + statut.slice(1);
  if (/valid|accept/i.test(statut)) {
    return <Chip label={txt} size="small" icon={<CheckCircleIcon style={{ color: "#22c55e" }} />} style={{ background: "#e9f9ef", color: "#22c55e", fontWeight: 700, border: "1.1px solid #22c55e" }} />;
  }
  if (/refus|rej/i.test(statut)) {
    return <Chip label={txt} size="small" icon={<CancelIcon style={{ color: "#ff3b3b" }} />} style={{ background: "#ffeaea", color: "#ff3b3b", fontWeight: 700, border: "1.1px solid #ff3b3b" }} />;
  }
  return <Chip label={txt} size="small" style={{ background: "#fff8e2", color: "#f59e0b", fontWeight: 700, border: "1.1px solid #fbbf24" }} />;
};

function Settings() {
  const [demandes, setDemandes] = useState([]);
  const [filteredDemandes, setFilteredDemandes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchDemandes = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          setError('Utilisateur non authentifié');
          return;
        }
        const response = await axios.get('https://appapi.unpaidfinancial.com/api/demandes', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDemandes(response.data);
        setFilteredDemandes(response.data);
      } catch (err) {
        setError('Erreur lors de la récupération des demandes');
        console.error(err);
      }
    };
    fetchDemandes();
  }, []);

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = demandes.filter((demande) =>
      (demande.nom || '').toLowerCase().includes(value) ||
      (demande.prenom || '').toLowerCase().includes(value)
    );
    setFilteredDemandes(filtered);
  };

  const updateDemandeStatus = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(
        `https://appapi.unpaidfinancial.com/api/admin/demandes/${id}/status`,
        { statut: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Mise à jour locale
      setFilteredDemandes(prev => prev.map(d =>
        d.id === id ? { ...d, statut: newStatus } : d
      ));
      setSuccess(`Demande ${newStatus} avec succès`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de la mise à jour");
      setTimeout(() => setError(''), 3500);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 1, pb: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 800, color: "#23253a", letterSpacing: ".3px", mb: 2, textAlign: 'center' }}>
        Gestion des demandes clients
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box mb={3} mt={2} sx={{ maxWidth: 480, mx: "auto" }}>
        <TextField
          label="Recherche par nom ou prénom"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={handleSearch}
          sx={{ bgcolor: "#fff", borderRadius: 2, boxShadow: "0 1.5px 10px rgba(44,44,92,0.03)" }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: "0 6px 24px 0 rgba(44,44,92,0.10)", maxHeight: "78vh" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Nom</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Prénom</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Téléphone</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Sexe</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Date de Naissance</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Adresse résidentielle</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Type d'aide</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Montant</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Projet</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Pays</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Ville</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Devise</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 800, background: "#f9fafb" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDemandes.map((demande) => (
              <TableRow key={demande.id} hover>
                <TableCell>{demande.nom}</TableCell>
                <TableCell>{demande.prenom}</TableCell>
                <TableCell>
                  <Tooltip title={demande.email}>
                    <span>{demande.email}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{demande.telephone}</TableCell>
                <TableCell>{demande.sexe}</TableCell>
                <TableCell>{demande.dateNaissance}</TableCell>
                <TableCell>{demande.adresse}</TableCell>
                <TableCell>{demande.typeAide}</TableCell>
                <TableCell>
                  {demande.montantDemande} {demande.devise}
                </TableCell>
                <TableCell>{demande.projetDescription}</TableCell>
                <TableCell>{demande.pays}</TableCell>
                <TableCell>{demande.ville}</TableCell>
                <TableCell>{demande.devise}</TableCell>
                <TableCell>
                  <StatusBadge statut={demande.statut} />
                </TableCell>
                <TableCell>
                  <ButtonGroup size="small" variant="contained">
                    <Button
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => updateDemandeStatus(demande.id, 'validé')}
                      disabled={/valid|accept/i.test(demande.statut)}
                    >
                      Valider
                    </Button>
                    <Button
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => updateDemandeStatus(demande.id, 'rejeté')}
                      disabled={/refus|rej/i.test(demande.statut)}
                    >
                      Rejeter
                    </Button>
                  </ButtonGroup>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}

export default Settings;
